import React, { useState, useRef } from 'react';
import { useSubmissions } from '../SubmissionsContext';

const CATEGORIES = ['Birthday', 'Achievement', 'Outing', 'Community', 'Home Care', 'Magic Moment'];

const s = {
  page: { padding: '20px 16px 24px', maxWidth: '540px', margin: '0 auto' },
  heading: { fontSize: '20px', fontWeight: '700', color: '#222', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#888', marginBottom: '24px' },
  card: { background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '12px', fontWeight: '700', color: '#DB3267', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  inputRow: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
  input: { flex: 1, padding: '11px 13px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '100%' },
  textarea: { flex: 1, padding: '11px 13px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '100%', minHeight: '80px', resize: 'vertical' },
  micBtn: {
    width: '44px', height: '44px', borderRadius: '50%', border: '1.5px solid #e0e0e0',
    background: '#fff', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  micBtnActive: { background: '#fce8ef', borderColor: '#DB3267' },
  catGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  catBtn: {
    padding: '10px 8px', borderRadius: '8px', border: '1.5px solid #e0e0e0',
    background: '#fff', fontSize: '13px', fontWeight: '500', color: '#555', textAlign: 'center',
  },
  catBtnActive: { background: '#fce8ef', borderColor: '#DB3267', color: '#DB3267', fontWeight: '700' },
  shiftRow: { display: 'flex', gap: '10px' },
  shiftBtn: {
    flex: 1, padding: '12px', borderRadius: '8px', border: '1.5px solid #e0e0e0',
    background: '#fff', fontSize: '14px', fontWeight: '600', color: '#555',
  },
  shiftBtnActive: { background: '#fce8ef', borderColor: '#DB3267', color: '#DB3267' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '12px' },
  photoSlot: {
    aspectRatio: '1', borderRadius: '8px', border: '1.5px dashed #e0e0e0',
    background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '22px', color: '#ccc', overflow: 'hidden', position: 'relative', cursor: 'pointer',
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  removePhoto: {
    position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px',
    background: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: '50%',
    fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  gdprRow: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  gdprCheck: { width: '20px', height: '20px', marginTop: '2px', flexShrink: 0, accentColor: '#DB3267' },
  gdprText: { fontSize: '13px', color: '#555', lineHeight: '1.5' },
  submitBtn: {
    width: '100%', padding: '15px', background: '#DB3267', color: '#fff',
    borderRadius: '10px', fontWeight: '700', fontSize: '16px',
  },
  submitBtnDisabled: { background: '#e0e0e0', color: '#aaa', cursor: 'not-allowed' },
  successCard: {
    background: '#e8f5e9', border: '1.5px solid #a5d6a7', borderRadius: '12px',
    padding: '28px 20px', textAlign: 'center', marginBottom: '16px',
  },
  successIcon: { fontSize: '48px', marginBottom: '12px' },
  successTitle: { fontSize: '18px', fontWeight: '700', color: '#2e7d32', marginBottom: '6px' },
  successSub: { fontSize: '14px', color: '#555', marginBottom: '16px' },
  newBtn: {
    padding: '12px 24px', background: '#DB3267', color: '#fff',
    borderRadius: '8px', fontWeight: '700', fontSize: '14px',
  },
};

function useSpeech(onResult) {
  const recogRef = useRef(null);
  const [listening, setListening] = useState(false);

  function toggle() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition is not supported in this browser. Try Chrome on Android.'); return; }
    if (listening) {
      recogRef.current && recogRef.current.stop();
      setListening(false);
      return;
    }
    const recog = new SpeechRecognition();
    recog.lang = 'en-GB';
    recog.interimResults = false;
    recog.onresult = e => { onResult(e.results[0][0].transcript); setListening(false); };
    recog.onerror = () => setListening(false);
    recog.onend = () => setListening(false);
    recogRef.current = recog;
    recog.start();
    setListening(true);
  }

  return { listening, toggle };
}

export default function Submit({ user }) {
  const { addSubmission } = useSubmissions();
  const [about, setAbout] = useState('');
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('');
  const [shift, setShift] = useState(user.shift || '');
  const [photos, setPhotos] = useState([]);
  const [gdpr, setGdpr] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const aboutSpeech = useSpeech(t => setAbout(prev => prev ? prev + ' ' + t : t));
  const captionSpeech = useSpeech(t => setCaption(prev => prev ? prev + ' ' + t : t));

  function compressPhoto(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1200;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else { width = Math.round(width * MAX / height); height = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve({ url: canvas.toDataURL('image/jpeg', 0.82), name: file.name });
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  function handlePhotos(e) {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    files.slice(0, remaining).forEach(async f => {
      const compressed = await compressPhoto(f);
      setPhotos(prev => [...prev, compressed]);
    });
    e.target.value = '';
  }

  function removePhoto(i) {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
  }

  const canSubmit = about.trim() && caption.trim() && category && shift && gdpr;

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    const result = await addSubmission({
      about,
      caption,
      category,
      shift,
      photos: photos,
      submittedBy: user.name,
      isDemo: user.isDemo || false,
    });
    setSubmitting(false);
    if (result && result.ok) {
      setSubmitted(true);
    } else {
      alert('Something went wrong saving your moment. Please try again.');
    }
  }

  function reset() {
    setAbout(''); setCaption(''); setCategory(''); setPhotos([]); setGdpr(false); setSubmitted(false);
  }

  if (submitted) {
    return (
      <div style={s.page}>
        <div style={s.successCard}>
          <div style={s.successIcon}>🌟</div>
          <div style={s.successTitle}>Moment submitted!</div>
          <div style={s.successSub}>Thank you {user.name.split(' ')[0]}! Your moment has been sent for review. You'll be notified when it's shared.</div>
          <button style={s.newBtn} onClick={reset}>Share another moment</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Share a Moment</h1>
      <p style={s.sub}>Capture something special from your shift</p>

      {/* Photos */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Photos (up to 5)</div>
        <div style={s.photoGrid}>
          {photos.map((p, i) => (
            <div key={i} style={s.photoSlot}>
              <img src={p.url} alt="" style={s.photoImg} />
              <div style={s.removePhoto} onClick={() => removePhoto(i)}>✕</div>
            </div>
          ))}
          {photos.length < 5 && (
            <div style={s.photoSlot} onClick={() => fileRef.current.click()}>+</div>
          )}
          {Array.from({ length: Math.max(0, 4 - photos.length) }).map((_, i) => (
            <div key={'empty' + i} style={s.photoSlot} />
          ))}
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotos} />
        <div style={{ fontSize: '12px', color: '#aaa' }}>Tap + to add photos from your camera or gallery</div>
      </div>

      {/* Who is this about */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Who is this moment about?</div>
        <div style={s.inputRow}>
          <input
            style={s.input}
            type="text"
            spellCheck={true}
            placeholder="e.g. David, Sarah & the Tuesday group"
            value={about}
            onChange={e => setAbout(e.target.value)}
          />
          <button
            style={{ ...s.micBtn, ...(aboutSpeech.listening ? s.micBtnActive : {}) }}
            onClick={aboutSpeech.toggle}
            title="Tap to dictate"
            type="button"
          >
            {aboutSpeech.listening ? '🔴' : '🎤'}
          </button>
        </div>
      </div>

      {/* Caption */}
      <div style={s.card}>
        <div style={s.sectionTitle}>What happened?</div>
        <div style={s.inputRow}>
          <textarea
            style={s.textarea}
            spellCheck={true}
            placeholder="Tell us about this moment..."
            value={caption}
            onChange={e => setCaption(e.target.value)}
          />
          <button
            style={{ ...s.micBtn, ...(captionSpeech.listening ? s.micBtnActive : {}) }}
            onClick={captionSpeech.toggle}
            title="Tap to dictate"
            type="button"
          >
            {captionSpeech.listening ? '🔴' : '🎤'}
          </button>
        </div>
      </div>

      {/* Category */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Category</div>
        <div style={s.catGrid}>
          {CATEGORIES.map(c => (
            <button
              key={c}
              style={{ ...s.catBtn, ...(category === c ? s.catBtnActive : {}) }}
              onClick={() => setCategory(c)}
              type="button"
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Shift */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Your Shift</div>
        <div style={s.shiftRow}>
          {['Day', 'Night'].map(sh => (
            <button
              key={sh}
              style={{ ...s.shiftBtn, ...(shift === sh ? s.shiftBtnActive : {}) }}
              onClick={() => setShift(sh)}
              type="button"
            >
              {sh === 'Day' ? '☀️' : '🌙'} {sh} Shift
            </button>
          ))}
        </div>
      </div>

      {/* GDPR */}
      <div style={s.card}>
        <label style={s.gdprRow}>
          <input type="checkbox" style={s.gdprCheck} checked={gdpr} onChange={e => setGdpr(e.target.checked)} />
          <span style={s.gdprText}>
            I confirm I have permission to share these photos and that no service user can be identified without their consent.
          </span>
        </label>
      </div>

      <button
        style={{ ...s.submitBtn, ...(!canSubmit || submitting ? s.submitBtnDisabled : {}) }}
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        type="button"
      >
        {submitting ? 'Submitting...' : 'Submit Moment'}
      </button>
    </div>
  );
}
